import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type { Adresse, Langue, OrigineContact, TypeContact } from '@artosera/shared';
import { api, requeteApi, type AppelApi } from './client.js';
import type { Pagination } from './types.js';
import { appelDeRequete, cleMetier, dansLaPortee, precedentDansLaPortee } from './cles.js';

/**
 * Contacts — `/v1/g/:slug/contacts` (`docs/dev/api.md` § 14). Les formes
 * suivent `apps/api/src/contacts/commun.ts` et `contacts.routes.ts` au champ
 * près : un seul type ici, jamais un par écran (même parti pris que
 * `api/oeuvres.ts`).
 */

export type GenreContact = 'personne' | 'organisation';

export interface ConsentementNewsletter {
  readonly accorde: boolean;
  readonly date: string | null;
  readonly source: OrigineContact | null;
  readonly retireLe: string | null;
}

export interface ContactPresente {
  readonly id: string;
  readonly genre: GenreContact;
  readonly nom: string;
  readonly prenom: string | null;
  readonly raisonSociale: string | null;
  readonly nomAffiche: string;
  readonly type: TypeContact;
  readonly adresses: readonly Adresse[];
  readonly emails: readonly string[];
  readonly telephones: readonly string[];
  readonly langue: Langue;
  readonly numeroTvaIntracom: string | null;
  readonly consentementNewsletter: ConsentementNewsletter;
  readonly notes: string;
  readonly etiquettes: readonly string[];
  readonly origine: OrigineContact;
  readonly archive: boolean;
  readonly anonymise: boolean;
  readonly anonymiseLe: string | null;
  readonly fusionneDans: string | null;
  readonly creeLe: string;
  readonly modifieLe: string;
  readonly score?: number | null;
}

export interface DemandeRecente {
  readonly id: string;
  readonly sujet: string;
  readonly etat: 'a_traiter' | 'repondue' | 'close';
  readonly source: string;
  readonly oeuvreId: string | null;
  readonly recueLe: string;
}

export interface ReservationRecente {
  readonly id: string;
  readonly oeuvreId: string;
  readonly etat: string;
  readonly jusquau: string;
}

export interface AgregatsContact {
  readonly demandes: { readonly total: number; readonly aTraiter: number };
  readonly reservations: { readonly total: number; readonly actives: number };
  readonly oeuvresDInteret: readonly {
    readonly id: string;
    readonly titre: string;
    readonly statut: string;
    readonly origine: string;
  }[];
}

export interface FicheContact {
  readonly contact: ContactPresente;
  readonly agregats: AgregatsContact;
  readonly demandes: readonly DemandeRecente[];
  readonly reservations: readonly ReservationRecente[];
}

export interface CorpsAdresse {
  libelle?: string;
  ligne1?: string;
  ligne2?: string;
  codePostal?: string;
  ville?: string;
  pays?: string;
}

export interface CorpsContact {
  genre?: GenreContact;
  nom: string;
  prenom?: string;
  raisonSociale?: string;
  type?: TypeContact;
  adresses?: CorpsAdresse[];
  emails?: string[];
  telephones?: string[];
  langue?: Langue;
  numeroTvaIntracom?: string;
  notes?: string;
  etiquettes?: string[];
  origine?: OrigineContact;
  consentementNewsletter?: boolean;
  sourceConsentement?: OrigineContact;
}

export type CorpsMiseAJourContact = Partial<CorpsContact>;

export interface FiltresContacts {
  q?: string;
  type?: readonly TypeContact[];
  etiquette?: string;
  origine?: OrigineContact;
  consentement?: boolean;
  inclureArchives?: boolean;
  tri?: 'nom' | 'recent' | 'ancien' | 'score';
  scoreMin?: number;
  page?: number;
  parPage?: number;
}

export interface ReponseListeContacts {
  readonly contacts: readonly ContactPresente[];
  readonly pagination: Pagination;
}

export interface ReponseFusion {
  readonly contact: ContactPresente;
  readonly absorbe: ContactPresente;
  readonly demandesDeplacees: number;
  readonly reservationsDeplacees: number;
}

export interface ReponseSuppressionRgpd {
  readonly contact: ContactPresente;
  readonly demandesAnonymisees: number;
  readonly reservationsAnonymisees: number;
}

/** Dossier de portabilité (7.7) — `apps/api/src/contacts/export.ts`. */
export interface DossierContact {
  readonly version: number;
  readonly genereLe: string;
  readonly galerie: { readonly slug: string; readonly nom: string };
  readonly contact: ContactPresente;
  readonly demandes: readonly unknown[];
  readonly reservations: readonly unknown[];
  readonly aVenir: readonly string[];
}

function requeteFiltres(filtres: FiltresContacts): string {
  const params = new URLSearchParams();
  if (filtres.q) params.set('q', filtres.q);
  if (filtres.type && filtres.type.length > 0) params.set('type', filtres.type.join(','));
  if (filtres.etiquette) params.set('etiquette', filtres.etiquette);
  if (filtres.origine) params.set('origine', filtres.origine);
  if (filtres.consentement !== undefined) params.set('consentement', String(filtres.consentement));
  if (filtres.inclureArchives) params.set('inclureArchives', 'true');
  params.set('tri', filtres.tri ?? 'nom');
  if (filtres.scoreMin !== undefined) params.set('scoreMin', String(filtres.scoreMin));
  params.set('page', String(filtres.page ?? 1));
  params.set('parPage', String(filtres.parPage ?? 50));
  return params.toString();
}

export const contactsApi = {
  lister: (galerie: string, filtres: FiltresContacts = {}, appel?: AppelApi) =>
    api.get<ReponseListeContacts>(
      `/g/${encodeURIComponent(galerie)}/contacts?${requeteFiltres(filtres)}`,
      { galerie, ...appel },
    ),
  obtenir: (galerie: string, id: string, appel?: AppelApi) =>
    api.get<FicheContact>(`/g/${encodeURIComponent(galerie)}/contacts/${id}`, {
      galerie,
      ...appel,
    }),
  creer: (galerie: string, corps: CorpsContact, appel?: AppelApi) =>
    api.post<{ contact: ContactPresente }>(`/g/${encodeURIComponent(galerie)}/contacts`, corps, {
      galerie,
      ...appel,
    }),
  modifier: (galerie: string, id: string, corps: CorpsMiseAJourContact, appel?: AppelApi) =>
    api.patch<{ contact: ContactPresente }>(
      `/g/${encodeURIComponent(galerie)}/contacts/${id}`,
      corps,
      { galerie, ...appel },
    ),
  remplacerEtiquettes: (
    galerie: string,
    id: string,
    etiquettes: readonly string[],
    appel?: AppelApi,
  ) =>
    api.put<{ contact: ContactPresente }>(
      `/g/${encodeURIComponent(galerie)}/contacts/${id}/etiquettes`,
      { etiquettes },
      { galerie, ...appel },
    ),
  poserConsentement: (
    galerie: string,
    id: string,
    corps: { accorde: boolean; source?: OrigineContact },
    appel?: AppelApi,
  ) =>
    api.post<{ contact: ContactPresente }>(
      `/g/${encodeURIComponent(galerie)}/contacts/${id}/consentement`,
      corps,
      { galerie, ...appel },
    ),
  archiver: (galerie: string, id: string, raison?: string, appel?: AppelApi) =>
    api.post<{ contact: ContactPresente }>(
      `/g/${encodeURIComponent(galerie)}/contacts/${id}/archiver`,
      raison ? { raison } : {},
      { galerie, ...appel },
    ),
  restaurer: (galerie: string, id: string, appel?: AppelApi) =>
    api.post<{ contact: ContactPresente }>(
      `/g/${encodeURIComponent(galerie)}/contacts/${id}/restaurer`,
      {},
      { galerie, ...appel },
    ),
  fusionner: (galerie: string, id: string, avec: string, appel?: AppelApi) =>
    api.post<ReponseFusion>(
      `/g/${encodeURIComponent(galerie)}/contacts/${id}/fusionner`,
      { avec },
      { galerie, ...appel },
    ),
  exporter: (galerie: string, id: string, appel?: AppelApi) =>
    api.get<DossierContact>(`/g/${encodeURIComponent(galerie)}/contacts/${id}/export`, {
      galerie,
      ...appel,
    }),
  // `api.delete` n'accepte pas de corps (le DELETE n'en porte presque
  // jamais) ; celui-ci en a un — `raison` — donc l'appel direct à
  // `requeteApi` plutôt qu'un détournement de type.
  supprimer: (galerie: string, id: string, raison: string, appel?: AppelApi) =>
    requeteApi<ReponseSuppressionRgpd>(`/g/${encodeURIComponent(galerie)}/contacts/${id}`, {
      method: 'DELETE',
      corps: { raison },
      galerie,
      ...appel,
    }),
};

const cleListe = (galerie: string, filtres: FiltresContacts) =>
  cleMetier(galerie, 'contacts', 'liste', filtres);
const cleFiche = (galerie: string, id: string) => cleMetier(galerie, 'contacts', 'fiche', id);

export function useContacts(
  galerie: string,
  filtres: FiltresContacts = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: cleListe(galerie, filtres),
    queryFn: (contexte) => contactsApi.lister(galerie, filtres, appelDeRequete(contexte)),
    placeholderData: precedentDansLaPortee(galerie),
    enabled: options.enabled ?? true,
  });
}

export function useContact(galerie: string, id: string | null) {
  return useQuery({
    queryKey: cleFiche(galerie, id ?? ''),
    queryFn: (contexte) => contactsApi.obtenir(galerie, id ?? '', appelDeRequete(contexte)),
    enabled: id !== null,
  });
}

function invaliderContacts(client: ReturnType<typeof useQueryClient>, galerie: string): void {
  void client.invalidateQueries({ queryKey: cleMetier(galerie, 'contacts', 'liste') });
}

export function useCreerContact(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: CorpsContact, appel) => contactsApi.creer(galerie, corps, appel),
      onSuccess: () => invaliderContacts(client, galerie),
    }),
  );
}

export function useModifierContact(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, corps }: { id: string; corps: CorpsMiseAJourContact }, appel) =>
        contactsApi.modifier(galerie, id, corps, appel),
      onSuccess: (_donnees, variables) => {
        invaliderContacts(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
      },
    }),
  );
}

export function useRemplacerEtiquettesContact(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, etiquettes }: { id: string; etiquettes: readonly string[] }, appel) =>
        contactsApi.remplacerEtiquettes(galerie, id, etiquettes, appel),
      onSuccess: (_donnees, variables) => {
        invaliderContacts(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
      },
    }),
  );
}

export function usePoserConsentementContact(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (
        {
          id,
          accorde,
          source,
        }: {
          id: string;
          accorde: boolean;
          source?: OrigineContact;
        },
        appel,
      ) => contactsApi.poserConsentement(galerie, id, { accorde, source }, appel),
      onSuccess: (_donnees, variables) => {
        invaliderContacts(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
      },
    }),
  );
}

export function useArchiverContact(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, raison }: { id: string; raison?: string }, appel) =>
        contactsApi.archiver(galerie, id, raison, appel),
      onSuccess: (_donnees, variables) => {
        invaliderContacts(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
      },
    }),
  );
}

export function useRestaurerContact(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (id: string, appel) => contactsApi.restaurer(galerie, id, appel),
      onSuccess: (_donnees, id) => {
        invaliderContacts(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, id) });
      },
    }),
  );
}

export function useFusionnerContact(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, avec }: { id: string; avec: string }, appel) =>
        contactsApi.fusionner(galerie, id, avec, appel),
      onSuccess: (_donnees, variables) => {
        invaliderContacts(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.avec) });
      },
    }),
  );
}

export function useSupprimerContactRgpd(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, raison }: { id: string; raison: string }, appel) =>
        contactsApi.supprimer(galerie, id, raison, appel),
      onSuccess: (_donnees, variables) => {
        invaliderContacts(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
      },
    }),
  );
}
